import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Calendar, Clock, MapPin, Edit, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const Audiencias = () => {
  const navigate = useNavigate();
  const [audiencias, setAudiencias] = useState([]);
  const [processos, setProcessos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingAudiencia, setEditingAudiencia] = useState(null);
  const [formData, setFormData] = useState({
    processo_id: '',
    tipo_audiencia: '',
    data_audiencia: '',
    hora_audiencia: '',
    local: '',
    observacoes: '',
    status: 'Agendada'
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [audienciasRes, processosRes] = await Promise.all([
        api.getAudiencias(),
        api.getProcessos()
      ]);
      setAudiencias(audienciasRes.data);
      setProcessos(processosRes.data);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Combinar data e hora
      const dataHora = `${formData.data_audiencia}T${formData.hora_audiencia}:00`;
      
      const submitData = {
        processo_id: formData.processo_id,
        tipo_audiencia: formData.tipo_audiencia,
        data_audiencia: dataHora,
        local: formData.local || null,
        observacoes: formData.observacoes || null,
        status: formData.status
      };
      
      if (editingAudiencia) {
        await api.updateAudiencia(editingAudiencia.id, submitData);
        toast.success('Audiência atualizada com sucesso!');
      } else {
        await api.createAudiencia(submitData);
        toast.success('Audiência agendada com sucesso!');
      }
      
      handleCloseDialog();
      loadData();
    } catch (error) {
      console.error('Erro ao salvar audiência:', error);
      toast.error('Erro ao salvar audiência');
    }
  };

  const handleEdit = (audiencia) => {
    setEditingAudiencia(audiencia);
    const dataHora = new Date(audiencia.data_audiencia);
    setFormData({
      processo_id: audiencia.processo_id,
      tipo_audiencia: audiencia.tipo_audiencia,
      data_audiencia: dataHora.toISOString().split('T')[0],
      hora_audiencia: dataHora.toTimeString().slice(0, 5),
      local: audiencia.local || '',
      observacoes: audiencia.observacoes || '',
      status: audiencia.status
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (audienciaId) => {
    if (!window.confirm('Deseja realmente excluir esta audiência?')) return;

    try {
      await api.deleteAudiencia(audienciaId);
      toast.success('Audiência excluída com sucesso!');
      loadData();
    } catch (error) {
      console.error('Erro ao excluir audiência:', error);
      toast.error('Erro ao excluir audiência');
    }
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingAudiencia(null);
    setFormData({
      processo_id: '',
      tipo_audiencia: '',
      data_audiencia: '',
      hora_audiencia: '',
      local: '',
      observacoes: '',
      status: 'Agendada'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      'Agendada': 'bg-blue-100 text-blue-700',
      'Realizada': 'bg-green-100 text-green-700',
      'Cancelada': 'bg-red-100 text-red-700',
      'Adiada': 'bg-amber-100 text-amber-700'
    };
    return colors[status] || 'bg-slate-100 text-slate-700';
  };

  const formatDateTime = (dateStr) => {
    const date = new Date(dateStr);
    return {
      date: date.toLocaleDateString('pt-BR'),
      time: date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    };
  };

  if (loading) {
    return <div className="p-8" data-testid="audiencias-loading">Carregando...</div>;
  }

  return (
    <div className="p-8 space-y-6" data-testid="audiencias-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900">Audiências</h1>
          <p className="text-slate-600 mt-1">Gerencie as audiências dos processos</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-slate-900 hover:bg-slate-800" data-testid="add-audiencia-button">
              <Plus className="w-4 h-4 mr-2" />
              Agendar Audiência
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingAudiencia ? 'Editar Audiência' : 'Agendar Nova Audiência'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="processo_id">Processo *</Label>
                <Select
                  value={formData.processo_id}
                  onValueChange={(value) => setFormData({ ...formData, processo_id: value })}
                  required
                >
                  <SelectTrigger data-testid="audiencia-processo-select">
                    <SelectValue placeholder="Selecione um processo" />
                  </SelectTrigger>
                  <SelectContent>
                    {processos.map((processo) => (
                      <SelectItem key={processo.id} value={processo.id}>
                        {processo.numero_processo} - {processo.cliente?.nome_completo}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="tipo_audiencia">Tipo de Audiência *</Label>
                <Select
                  value={formData.tipo_audiencia}
                  onValueChange={(value) => setFormData({ ...formData, tipo_audiencia: value })}
                  required
                >
                  <SelectTrigger data-testid="audiencia-tipo-select">
                    <SelectValue placeholder="Selecione o tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Inicial">Inicial</SelectItem>
                    <SelectItem value="Instrução">Instrução</SelectItem>
                    <SelectItem value="Conciliação">Conciliação</SelectItem>
                    <SelectItem value="Julgamento">Julgamento</SelectItem>
                    <SelectItem value="Outra">Outra</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="data_audiencia">Data *</Label>
                  <Input
                    id="data_audiencia"
                    type="date"
                    required
                    value={formData.data_audiencia}
                    onChange={(e) => setFormData({ ...formData, data_audiencia: e.target.value })}
                    data-testid="audiencia-data-input"
                  />
                </div>
                <div>
                  <Label htmlFor="hora_audiencia">Hora *</Label>
                  <Input
                    id="hora_audiencia"
                    type="time"
                    required
                    value={formData.hora_audiencia}
                    onChange={(e) => setFormData({ ...formData, hora_audiencia: e.target.value })}
                    data-testid="audiencia-hora-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="local">Local</Label>
                <Input
                  id="local"
                  placeholder="Ex: Fórum da Comarca, Sala Virtual"
                  value={formData.local}
                  onChange={(e) => setFormData({ ...formData, local: e.target.value })}
                />
              </div>

              <div>
                <Label htmlFor="status">Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData({ ...formData, status: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Agendada">Agendada</SelectItem>
                    <SelectItem value="Realizada">Realizada</SelectItem>
                    <SelectItem value="Cancelada">Cancelada</SelectItem>
                    <SelectItem value="Adiada">Adiada</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="observacoes">Observações</Label>
                <textarea
                  id="observacoes"
                  className="w-full min-h-[80px] p-2 border rounded-md"
                  placeholder="Informações adicionais sobre a audiência..."
                  value={formData.observacoes}
                  onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                />
              </div>

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={handleCloseDialog}>
                  Cancelar
                </Button>
                <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="save-audiencia-button">
                  {editingAudiencia ? 'Atualizar' : 'Agendar'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-4">
        {audiencias.length > 0 ? (
          audiencias.map((audiencia) => {
            const { date, time } = formatDateTime(audiencia.data_audiencia);
            return (
              <Card key={audiencia.id} className="hover:shadow-lg transition-shadow" data-testid="audiencia-card">
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <Calendar className="w-5 h-5 text-slate-600" />
                        <CardTitle className="text-lg">{audiencia.tipo_audiencia}</CardTitle>
                        <span className={`text-xs px-2 py-1 rounded ${getStatusColor(audiencia.status)}`}>
                          {audiencia.status}
                        </span>
                      </div>
                      <p className="text-sm text-slate-600 mt-2">
                        Processo: {audiencia.processo?.numero_processo}
                      </p>
                      <p className="text-sm text-slate-600">
                        Cliente: {audiencia.cliente?.nome_completo}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/processos/${audiencia.processo_id}`)}
                      >
                        Ver Processo
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(audiencia)}
                        data-testid="edit-audiencia-button"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(audiencia.id)}
                        data-testid="delete-audiencia-button"
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-6 text-sm text-slate-600">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      {date}
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      {time}
                    </div>
                    {audiencia.local && (
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        {audiencia.local}
                      </div>
                    )}
                  </div>
                  {audiencia.observacoes && (
                    <p className="text-sm text-slate-600 mt-3 p-3 bg-slate-50 rounded">
                      {audiencia.observacoes}
                    </p>
                  )}
                </CardContent>
              </Card>
            );
          })
        ) : (
          <div className="text-center py-12 text-slate-500">
            Nenhuma audiência agendada
          </div>
        )}
      </div>
    </div>
  );
};

export default Audiencias;
